import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useHeaderNotifications, useNotificationsSubscription } from '@/hooks/useNotifications'
import {
  notificationTypeColors,
} from '@/types/notification'
import type { Notification, NotificationType } from '@/types/notification'
import {
  Bell,
  FileText,
  CreditCard,
  FolderKanban,
  AlertTriangle,
  Info,
  Check,
  MessageSquare,
} from 'lucide-react'

// STORY-033: Composant cloche de notifications pour le header

const defaultColors = { bg: 'bg-beige-100', icon: 'text-beige-600' }

const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case 'ticket':
      return MessageSquare
    case 'devis':
      return FileText
    case 'payment':
      return CreditCard
    case 'project':
      return FolderKanban
    case 'reminder':
      return AlertTriangle
    case 'system':
    default:
      return Info
  }
}

const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return "A l'instant"
  if (diffMins < 60) return `Il y a ${diffMins} min`
  if (diffHours < 24) return `Il y a ${diffHours}h`
  if (diffDays < 7) return `Il y a ${diffDays}j`
  return date.toLocaleDateString('fr-FR')
}

interface NotificationItemProps {
  notification: Notification
  onMarkAsRead: (id: string) => void
  onClick: () => void
}

function NotificationItem({ notification, onMarkAsRead, onClick }: NotificationItemProps) {
  const Icon = getNotificationIcon(notification.type)
  const colors = notificationTypeColors[notification.type] || defaultColors

  const handleClick = () => {
    if (!notification.read) {
      onMarkAsRead(notification.id)
    }
    onClick()
  }

  return (
    <button
      onClick={handleClick}
      className={`w-full px-4 py-3 text-left hover:bg-beige-50 transition-colors flex items-start gap-3 ${
        !notification.read ? 'bg-mint-50/50' : ''
      }`}
    >
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${colors.bg}`}>
        <Icon className={`w-4 h-4 ${colors.icon}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm font-medium text-brown-900 truncate ${!notification.read ? 'font-semibold' : ''}`}>
            {notification.title || notification.message}
          </p>
          {!notification.read && (
            <span className="w-2 h-2 rounded-full bg-mint-500 flex-shrink-0 mt-1.5" />
          )}
        </div>
        {notification.title && (
          <p className="text-sm text-brown-600 line-clamp-2 mt-0.5">
            {notification.message}
          </p>
        )}
        <p className="text-xs text-brown-400 mt-1">
          {formatTimeAgo(notification.created_at)}
        </p>
      </div>
    </button>
  )
}

export default function NotificationBell() {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const {
    count,
    notifications,
    isLoading,
    markAsRead,
    markAllAsRead,
    isMarkingAllAsRead,
  } = useHeaderNotifications()

  // S'abonner aux nouvelles notifications
  const handleNewNotification = useCallback((notification: Notification) => {
    console.log('Nouvelle notification:', notification)
  }, [])

  useNotificationsSubscription(handleNewNotification)

  // Fermer le dropdown quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleNotificationClick = (notification: Notification) => {
    setIsOpen(false)

    // Naviguer vers le lien associé
    if (notification.link) {
      navigate(notification.link)
    }
  }

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead()
    } catch (error) {
      console.error('Erreur:', error)
    }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-brown-600 hover:text-brown-900 hover:bg-beige-50 rounded-lg transition-colors"
      >
        <Bell className="w-5 h-5" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-mint-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-lg border border-beige-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-beige-200 flex items-center justify-between bg-beige-50">
            <h3 className="font-semibold text-brown-900">Notifications</h3>
            {count > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                disabled={isMarkingAllAsRead}
                className="text-sm text-mint-600 hover:text-mint-700 font-medium flex items-center gap-1 disabled:opacity-50"
              >
                <Check className="w-4 h-4" />
                Tout marquer lu
              </button>
            )}
          </div>

          {/* Notifications list */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="py-8 text-center text-brown-500">
                <div className="animate-spin w-6 h-6 border-2 border-mint-500 border-t-transparent rounded-full mx-auto" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-8 text-center text-brown-500">
                <Bell className="w-8 h-8 mx-auto mb-2 text-beige-300" />
                <p className="text-sm">Aucune notification</p>
              </div>
            ) : (
              <div className="divide-y divide-beige-100">
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={markAsRead}
                    onClick={() => handleNotificationClick(notification)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-beige-200 bg-beige-50">
              <button
                onClick={() => {
                  setIsOpen(false)
                  navigate('/notifications')
                }}
                className="w-full text-center text-sm text-mint-600 hover:text-mint-700 font-medium py-1"
              >
                Voir toutes les notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
